import { AssertionDescriptors, CaseFileStaticItems } from '../../../utilities/Constants';
import { IAssertionDescriptorValidation, IStaticItemValidation } from './SameCaseEasyUpdate';
import { ICaseAssertionModel, ICaseStatusModel } from '../../../interfaces/ICase';
import { IMultiSelectOption, IStaticItemLookupModel } from '../../../interfaces/ILookup';
import { useEffect, useState } from 'react';

import { AssertionDescriptorInputControl } from '../../../components/case/AssertionDescriptorInputControl';
import CaseDataCustomLogicHelper from '../../../utilities/CaseDataCustomLogicHelper';
import CaseFileCheckmateSelectHelper from '../../../utilities/CaseFileCheckmateSelectHelper';
import CaseHelper from '../../../utilities/CaseHelper';
import { CaseNarrativeModal } from '../../../components/case/dialogs/CaseNarrativeModal';
import { IAssertionDescriptorModel } from '../../../interfaces/IAssertionDescriptor';
import { IOrganizationBaseModel } from '../../../interfaces/IOrganization';
import { ISameCaseEasyUpdateCaseModel } from './ISameCaseEasyUpdateModel';
import { IconButton } from '../../../shared/buttons/IconButton';
import { JSX } from 'react';
import { Stack } from 'react-bootstrap';
import { StaticItemInputControl } from '../../../components/quad/same-case-easy-update/input-controls/StaticItemInputControl';
import { cloneDeep } from 'lodash';
import { useHover } from '@mantine/hooks';

interface ISelectedFieldForZoneProps {
    actionButtons: JSX.Element;
    assertionDescriptors: IAssertionDescriptorModel[];
    caseModel?: ISameCaseEasyUpdateCaseModel;
    assertionDescriptorGuid?: string;
    isFirstItem: boolean;
    isLastItem: boolean;
    onApplyToAllApplicableAssertionDescriptors: (
        assertionDescriptorGuid: string,
        caseAssertions?: ICaseAssertionModel[]
    ) => void;
    onApplyToAllApplicableStaticItems: (zoneGuid: string, staticItemId: number) => void;
    onZoneCaseDataChange: (
        zoneGuid: string,
        newCaseModel: ISameCaseEasyUpdateCaseModel,
        refreshFields: boolean
    ) => void;
    rowLabel?: string;
    staticItems: IStaticItemLookupModel[];
    staticItemId: number;
    validation: {
        assertionDescriptorValidation: IAssertionDescriptorValidation;
        staticItemValidation: IStaticItemValidation;
    };
    zone: IOrganizationBaseModel;
    zonesWithErrors: string[];
}

export function SelectedFieldForZone(props: ISelectedFieldForZoneProps) {
    const [hovered, setHovered] = useState<boolean>(false);
    const [currentNarrativeCaseAssertion, setCurrentNarrativeCaseAssertion] =
        useState<ICaseAssertionModel | null>(null);

    const assertionDescriptor = props.assertionDescriptors.find(
        (ad) => ad.guid === props.assertionDescriptorGuid
    );

    const handleAssertionDescriptorApplyAllClick = (
        assertionDescriptor: IAssertionDescriptorModel
    ) => {
        if (!props.assertionDescriptorGuid) return;

        const caseAssertions = props.caseModel?.caseAssertions?.filter(
            (ca) => ca.assertionDescriptorGuid === assertionDescriptor.guid
        );

        props.onApplyToAllApplicableAssertionDescriptors(
            props.assertionDescriptorGuid,
            caseAssertions
        );
    };

    const handleStaticItemApplyAllClick = (zoneGuid: string, staticItemId: number) => {
        if (!props.staticItemId) return;
        props.onApplyToAllApplicableStaticItems(zoneGuid, staticItemId);
    };

    const handleAssertionDescriptorSingleSelectChange = (
        selectedOption: IMultiSelectOption,
        assertionDescriptorGuid: string,
        isBoolean?: boolean
    ) => {
        if (!props.caseModel || !props.zone.guid) return;

        const updatedCaseCopy =
            CaseFileCheckmateSelectHelper.updateCaseOnSelectedAssertionDescriptorValueChange(
                cloneDeep(props.caseModel),
                assertionDescriptorGuid,
                isBoolean || false,
                selectedOption,
                props.assertionDescriptors
            ) as ISameCaseEasyUpdateCaseModel;

        CaseDataCustomLogicHelper.Run(updatedCaseCopy, props.assertionDescriptors);
        props.onZoneCaseDataChange(props.zone.guid, updatedCaseCopy, false);
    };

    const handleStaticItemChange = (staticItemId: number, value: any) => {
        const caseCopy = cloneDeep(props.caseModel);
        if (!caseCopy || !props.zone.guid) return;

        switch (staticItemId) {
            case CaseFileStaticItems.CaseStatus.Id: {
                const lookupItem = value as ICaseStatusModel;
                if (value && value.id) {
                    caseCopy.caseStatus = JSON.parse(JSON.stringify(lookupItem));
                } else {
                    caseCopy.caseStatus = undefined;
                }
                break;
            }
            case CaseFileStaticItems.CaseManager.Id: {
                caseCopy.caseManagerUser = value;
                break;
            }
            case CaseFileStaticItems.AlternateCaseManager.Id: {
                caseCopy.alternateCaseManagerUser = value;
                break;
            }
            case CaseFileStaticItems.CasePriority.Id: {
                caseCopy.priority = value;
                break;
            }
            case CaseFileStaticItems.LocalCounsel.Id: {
                // If first ever time adding any Defense Counsel; just add
                const selectedDefenseCounsel = value as IMultiSelectOption | undefined;

                if (!caseCopy.defenseCounsels && selectedDefenseCounsel?.guid) {
                    if (selectedDefenseCounsel?.guid) {
                        caseCopy.defenseCounsels = [
                            {
                                guid: selectedDefenseCounsel.guid,
                                priority: 1,
                                name: selectedDefenseCounsel.label,
                            },
                        ];
                    }
                    break;
                }
                // If no Local Counsel, Add
                const localCounselMatch = caseCopy.defenseCounsels?.find((x) => x.priority === 1);
                if (!localCounselMatch) {
                    if (selectedDefenseCounsel?.guid) {
                        caseCopy.defenseCounsels?.push({
                            guid: selectedDefenseCounsel.guid,
                            priority: 1,
                            name: selectedDefenseCounsel.label,
                        });
                    }
                } else {
                    if (selectedDefenseCounsel?.guid) {
                        localCounselMatch.guid = selectedDefenseCounsel.guid;
                        localCounselMatch.name = selectedDefenseCounsel.label;
                    } else {
                        const matchIndex = caseCopy.defenseCounsels?.findIndex(
                            (x) => x.priority === 1
                        );
                        if (matchIndex && matchIndex > -1) {
                            caseCopy.defenseCounsels?.splice(matchIndex, 1);
                        }
                    }
                }
                break;
            }
        }

        CaseDataCustomLogicHelper.Run(caseCopy, props.assertionDescriptors);

        const refreshFields = staticItemId === CaseFileStaticItems.CaseStatus.Id;
        props.onZoneCaseDataChange(props.zone.guid, caseCopy, refreshFields);
    };

    const handleAssertionDescriptorMultiSelectChange = (
        options: IMultiSelectOption[],
        assertionDescriptorId: string
    ) => {
        if (!props.caseModel || !props.zone.guid) return;

        const caseCopy = cloneDeep(props.caseModel);
        const adMatch = props.assertionDescriptors.find((ad) => ad.guid === assertionDescriptorId);

        if (!caseCopy?.caseAssertions) caseCopy.caseAssertions = [];

        caseCopy.caseAssertions = caseCopy.caseAssertions?.filter(
            (ca) => ca.assertionDescriptorGuid !== assertionDescriptorId
        );

        const matchingValues = adMatch?.assertionDescriptorValues?.filter((adv) =>
            options.some((opt) => opt.value === adv.guid)
        );

        if (adMatch) {
            const newCaseAssertions: ICaseAssertionModel[] =
                matchingValues?.map((match) => ({
                    assertionDescriptorGuid: adMatch.guid,
                    assertionDescriptorValueGuid: match.guid,
                })) ?? [];

            caseCopy.caseAssertions = caseCopy.caseAssertions.concat(newCaseAssertions);
        }

        CaseDataCustomLogicHelper.Run(caseCopy, props.assertionDescriptors);
        props.onZoneCaseDataChange(props.zone.guid, caseCopy, false);
    };

    const handleNarrativeChange = (assertionDescriptorId: string, value: string) => {
        const caseCopy = cloneDeep(props.caseModel);
        if (!caseCopy || !props.zone.guid) return;

        const foundAD = props.assertionDescriptors.find((ad) => ad.guid === assertionDescriptorId);

        if (!foundAD) {
            return;
        }

        const caseAssertion = caseCopy.caseAssertions?.find(
            (ca) => ca.assertionDescriptorGuid === assertionDescriptorId
        );

        if (caseAssertion) {
            caseAssertion.userOverride = true;
            CaseHelper.setCaseAssertionValue(caseAssertion, foundAD, value);
        } else {
            const newCaseAssertion: ICaseAssertionModel = {
                assertionDescriptorGuid: foundAD?.guid,
                caseGuid: props.caseModel?.guid,
                userOverride: true,
            };

            CaseHelper.setCaseAssertionValue(newCaseAssertion, foundAD, value);

            if (caseCopy.caseAssertions) {
                caseCopy.caseAssertions.push(newCaseAssertion);
            } else {
                caseCopy.caseAssertions = [newCaseAssertion];
            }
        }

        CaseDataCustomLogicHelper.Run(caseCopy, props.assertionDescriptors);
        props.onZoneCaseDataChange(props.zone.guid, caseCopy, false);
    };

    const handleSelectNarrativeCaseAssertion = (ca: ICaseAssertionModel) => {
        setCurrentNarrativeCaseAssertion(ca);
    };

    const handleTextValueChange = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const caseCopy = cloneDeep(props.caseModel);
        if (!caseCopy || !props.zone.guid) return;

        const { name: assertionDescriptorGuid, value, type } = event.target;

        const foundAD = props.assertionDescriptors.find(
            (ad) => ad.guid === assertionDescriptorGuid
        );

        if (!foundAD) return;

        const caseAssertionIdx = caseCopy.caseAssertions?.findIndex(
            (ca) => ca.assertionDescriptorGuid === assertionDescriptorGuid
        );

        let cleanedValue = value;

        if (type === 'number' || type === 'currency') {
            cleanedValue = value.replace(/[^0-9-]+/g, '');
        }

        if (caseAssertionIdx && caseAssertionIdx > -1) {
            const caseAssertion = caseCopy.caseAssertions?.find(
                (ca) => ca.assertionDescriptorGuid === assertionDescriptorGuid
            );

            if (caseAssertion) {
                const caseAssertionCopy = { ...caseAssertion, userOverride: true };
                CaseHelper.setCaseAssertionValue(caseAssertionCopy, foundAD, cleanedValue);
                caseCopy.caseAssertions?.splice(caseAssertionIdx, 1, caseAssertionCopy);
            }
        } else {
            const newCaseAssertion: ICaseAssertionModel = {
                assertionDescriptorGuid: foundAD?.guid,
                userOverride: true,
            };

            CaseHelper.setCaseAssertionValue(newCaseAssertion, foundAD, cleanedValue);
            if (caseCopy.caseAssertions) {
                caseCopy.caseAssertions.push(newCaseAssertion);
            } else {
                caseCopy.caseAssertions = [newCaseAssertion];
            }
        }

        CaseDataCustomLogicHelper.Run(caseCopy, props.assertionDescriptors);
        props.onZoneCaseDataChange(props.zone.guid, caseCopy, false);
    };

    // render
    const applyToAllStyles = {
        opacity: hovered ? 1 : 0,
        transition: 'opacity 0.25s ease' /* smooth transition */,
    };

    const caseNarrativeAD = props.assertionDescriptors.find(
        (ad) => ad.guid === currentNarrativeCaseAssertion?.assertionDescriptorGuid
    );

    const zoneHasErrors = props.zonesWithErrors.includes(props.zone.guid ?? '');

    if (assertionDescriptor && props.caseModel) {
        const zoneADErrors = props.validation.assertionDescriptorValidation[props.zone.guid ?? ''];
        const adErrors = zoneADErrors?.[assertionDescriptor.guid ?? ''];

        const uneditableFields = [
            AssertionDescriptors.InjuredPartyFirstName.Guid,
            AssertionDescriptors.InjuredPartyLastName.Guid,
            AssertionDescriptors.Jurisdiction.Guid,
        ];

        const isUneditableField = uneditableFields.includes(
            assertionDescriptor.guid?.toUpperCase() ?? ''
        );

        return (
            <>
                <RowRenderer
                    actionButtons={props.actionButtons}
                    control={
                        <Stack direction="vertical" gap={1}>
                            <Stack direction="horizontal" gap={2}>
                                <div
                                    className={`zone-wrapper ${zoneHasErrors ? 'zone-error-border' : ''}`}
                                >
                                    <AssertionDescriptorInputControl
                                        currentCase={props.caseModel}
                                        assertionDescriptors={props.assertionDescriptors}
                                        assertionDescriptor={assertionDescriptor}
                                        onChangeADTextValue={handleTextValueChange}
                                        onSearchableSingleSelectChange={
                                            handleAssertionDescriptorSingleSelectChange
                                        }
                                        onSearchableMultiSelectChange={
                                            handleAssertionDescriptorMultiSelectChange
                                        }
                                        onSelectNarrativeCaseAssertion={
                                            handleSelectNarrativeCaseAssertion
                                        }
                                        readonly={!props.caseModel.canEdit || isUneditableField}
                                    />
                                </div>
                                {!(props.isFirstItem && props.isLastItem) && !isUneditableField && (
                                    <div style={applyToAllStyles}>
                                        <IconButton
                                            buttonCssClass="btn btn-default btn-sm btn-no-bg"
                                            onClick={() =>
                                                handleAssertionDescriptorApplyAllClick(
                                                    assertionDescriptor
                                                )
                                            }
                                            icon="fal fa-check"
                                            text="Apply to All Zones"
                                            buttonTabIndex={-1}
                                        />
                                    </div>
                                )}
                            </Stack>
                            {adErrors && <span className="text-danger">{adErrors}</span>}
                        </Stack>
                    }
                    isFirstItem={props.isFirstItem}
                    isLastItem={props.isLastItem}
                    onHoverToggle={setHovered}
                    rowLabel={props.rowLabel}
                    zoneName={props.zone.name}
                />

                {currentNarrativeCaseAssertion && (
                    <CaseNarrativeModal
                        title={caseNarrativeAD?.displayName ?? ''}
                        assertion={currentNarrativeCaseAssertion}
                        validation={{}}
                        onSubmit={(value: string) => {
                            handleNarrativeChange(
                                currentNarrativeCaseAssertion?.assertionDescriptorGuid ?? '',
                                value
                            );
                            setCurrentNarrativeCaseAssertion(null);
                        }}
                        onCancel={() => setCurrentNarrativeCaseAssertion(null)}
                    />
                )}
            </>
        );
    }

    const staticItem = props.staticItems.find((si) => si.id === props.staticItemId);

    if (staticItem) {
        const zoneStaticItemErrors = props.validation.staticItemValidation[props.zone.guid ?? ''];
        const staticItemErrors = zoneStaticItemErrors?.[staticItem?.id ?? 0];

        const uneditableFields = [CaseFileStaticItems.CaseNumber.Id];

        const isUneditableField = uneditableFields.includes(staticItem.id);

        return (
            <RowRenderer
                actionButtons={props.actionButtons}
                control={
                    <Stack direction="vertical" gap={1}>
                        <Stack direction="horizontal" gap={2}>
                            <div
                                className={`zone-wrapper ${zoneHasErrors ? 'zone-error-border' : ''}`}
                            >
                                <StaticItemInputControl
                                    staticItemId={staticItem.id}
                                    zoneGuid={props.zone.guid ?? ''}
                                    caseModel={props.caseModel}
                                    onChange={handleStaticItemChange}
                                    readonly={isUneditableField}
                                />
                            </div>
                            {!(props.isFirstItem && props.isLastItem) && !isUneditableField && (
                                <div style={applyToAllStyles}>
                                    <IconButton
                                        buttonCssClass="btn btn-default btn-sm btn-no-bg"
                                        onClick={() =>
                                            handleStaticItemApplyAllClick(
                                                props.zone.guid ?? '',
                                                staticItem.id
                                            )
                                        }
                                        icon="fal fa-check"
                                        text="Apply to All Zones"
                                        buttonTabIndex={-1}
                                    />
                                </div>
                            )}
                        </Stack>
                        {staticItemErrors && (
                            <span className="text-danger">{staticItemErrors}</span>
                        )}
                    </Stack>
                }
                isFirstItem={props.isFirstItem}
                isLastItem={props.isLastItem}
                onHoverToggle={setHovered}
                rowLabel={props.rowLabel}
                zoneName={props.zone.name}
            />
        );
    }

    return (
        <div className={`zone-wrapper ${zoneHasErrors ? 'zone-error-border' : ''}`}>
            <RowRenderer
                actionButtons={props.actionButtons}
                control={<>Not Found</>}
                isFirstItem={props.isFirstItem}
                isLastItem={props.isLastItem}
                onHoverToggle={setHovered}
                rowLabel={props.rowLabel}
                zoneName={props.zone.name}
            />
        </div>
    );
}

function RowRenderer(props: {
    actionButtons: JSX.Element;
    control: JSX.Element;
    isFirstItem: boolean;
    isLastItem: boolean;
    onHoverToggle: (hovered: boolean) => void;
    rowLabel?: string;
    zoneName?: string;
}) {
    const { hovered, ref } = useHover();

    const paddingStyle = {
        paddingBottom: '0.5em',
        paddingTop: '0.5em',
        borderBottom: props.isLastItem ? '1px solid lightgray' : '',
    };

    useEffect(() => {
        props.onHoverToggle(hovered);
    }, [hovered]);

    return (
        <tr className="no-border" ref={ref}>
            <td style={paddingStyle}>{props.rowLabel}</td>
            <td style={paddingStyle}>{props.zoneName}</td>
            <td style={paddingStyle}>{props.control}</td>
            <td style={paddingStyle}>{props.actionButtons}</td>
        </tr>
    );
}
